import { MinusIcon, PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils";

type SetValue = (value: number | ((prevValue: number) => number)) => void;

export type RechargeInputProps = {
  value: number;
  onChange: SetValue;
};

const moneyFormatter = Intl.NumberFormat("pt-BR", {
  currency: "BRL",
  currencyDisplay: "symbol",
  currencySign: "standard",
  style: "currency",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PRESET_VALUES = [50, 100, 200, 500];

export function RechargeInput({
  value,
  onChange: setValue,
}: RechargeInputProps) {
  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.replace(/[^0-9]/g, "");
    const numericValue = parseInt(inputValue, 10);

    setValue(numericValue / 100);
  };

  const handlePresetAmount = (presetValue: number) => {
    setValue(presetValue);
  };

  const handleIncrement = () => {
    setValue(value + 10);
  };

  const handleDecrement = () => {
    setValue(Math.max(value - 10, 0));
  };

  return (
    <>
      <div className="recharge-input relative py-4">
        <div className="absolute inset-0 flex items-center pl-4 h-full pointer-events-none">
          R$
        </div>

        <Input
          className="h-16 pl-12 text-lg font-bold"
          type="text"
          value={moneyFormatter.format(value).replace(/R\$\s*/, "")}
          onChange={handleValueChange}
        />

        <div className="controls flex p-4 gap-2 h-full items-center absolute right-0 top-0">
          <Button
            type="button"
            variant="outline"
            className="h-6 w-6 rounded-full"
            size="icon"
            onClick={handleDecrement}
          >
            <MinusIcon className="w-3 h-3" />
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-6 w-6 rounded-full"
            size="icon"
            onClick={handleIncrement}
          >
            <PlusIcon className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mt-2">
        {PRESET_VALUES.map((presetValue) => {
          const isActive = value === presetValue;

          return (
            <Button
              key={presetValue}
              variant="outline"
              className={cn("border", isActive && "border-primary")}
              type="button"
              onClick={() => handlePresetAmount(presetValue)}
            >
              R$ {presetValue}
            </Button>
          );
        })}
      </div>
    </>
  );
}
